import { Button, styled, CircularProgress, useTheme, Box } from '@mui/material';
import { ExportSquare } from 'iconsax-react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Styled components
const CustomButton = styled(Button)(({ theme }) => ({
  fontSize: theme.spacing(4),
  fontWeight: 600,
  padding: theme.spacing(3),
  textTransform: 'capitalize',
  borderRadius: theme.spacing(2),
  boxShadow: 'none',
  '&:hover a': { color: 'initial !important' },
  '&:disabled': { color: '#fff' },
  // lineHeight: 1.5,
  [theme.breakpoints.down('xl')]: {
    padding: theme.spacing(2.25),
    fontSize: `${theme.spacing(3.5)} !important`,
  },
  [theme.breakpoints.down('lg')]: {
    // fontSize: `${theme.spacing()} !important`,
  },
}));

const CustomButtonLink = styled(Link)(({ theme }) => ({
  fontSize: theme.spacing(4),
  fontWeight: 600,
  color: theme.palette.common.white,
  padding: theme.spacing(4, 3),
  textAlign: 'center',
  borderRadius: theme.spacing(1),
  display: 'block',
  textDecoration: 'none',
}));

const ExportSquareCustom = styled(ExportSquare)(({ theme }) => ({
  marginLeft: theme.direction === 'ltr' ? theme.spacing(3) : '0',
  marginRight: theme.direction === 'rtl' ? theme.spacing(3) : '0',
}));

// Helper functions for generating colors
const generateColorStyles = (color, variant = 'contained', theme) => {
  const colorMapping = {
    main: {
      contained: {
        bg: {
          light: theme.palette.secondary.main,
          dark: theme.palette.secondary.main,
        },
        text: {
          light: theme.palette.secondary.contrastText,
          dark: theme.palette.secondary.contrastTextBlack,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: '#0364A1',
            dark: '#3AADF5',
          },
          text: {
            light: 'white',
            dark: theme.palette.secondary.contrastTextBlack,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: { light: '#0486D7', dark: '#70C4F7' },
        border: {
          light: '1px solid #0486D7',
          dark: '1px solid #70C4F7',
        },
        hover: {
          bg: {
            light: '#E1F2FD',
            dark: '#012135',
          },
          text: { light: '#0486D7', dark: '#70C4F7' },
          border: {
            light: '1px solid #0486D7',
            dark: '1px solid #70C4F7',
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: {
          light: theme.palette.secondary.main,
          dark: theme.palette.secondary.main,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: '#E1F2FD', dark: '#012135' },
          text: {
            light: theme.palette.secondary.secondary_10,
            dark: theme.palette.secondary.secondary_50,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    gray: {
      contained: {
        bg: { light: theme.palette.grey[200], dark: theme.palette.grey[700] },
        text: {
          light: theme.palette.text.primary,
          dark: theme.palette.text.primary,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: theme.palette.grey[300], dark: theme.palette.grey[600] },
          text: { light: 'white', dark: 'white' },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: {
          light: theme.palette.background.default,
          dark: theme.palette.background.paper,
        },
        text: {
          light: theme.palette.text.primary,
          dark: theme.palette.text.primary,
        },
        border: {
          light: `1px solid ${theme.palette.background.default}`,
          dark: `1px solid ${theme.palette.background.paper}`,
        },
        hover: {
          bg: { light: theme.palette.grey[500], dark: theme.palette.grey[700] },
          text: { light: theme.palette.text.secondary, dark: 'white' },
          border: {
            light: `1px solid ${theme.palette.grey[500]}`,
            dark: `1px solid ${theme.palette.grey[700]}`,
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: {
          light: theme.palette.text.primary,
          dark: theme.palette.text.primary,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: 'transparent', dark: 'transparent' },
          text: {
            light: theme.palette.text.secondary,
            dark: theme.palette.text.secondary,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    success: {
      contained: {
        bg: {
          light: '#389355',
          dark: '#389355',
        },
        text: {
          light: theme.palette.success.contrastText,
          dark: theme.palette.success.contrastText,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: '#2A6F40',
            dark: '#2A6F40',
          },
          text: { light: 'white', dark: 'white' },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: {
          light: 'transparent',
          dark: 'transparent',
        },
        text: {
          light: '#389355',
          dark: '#389355',
        },
        border: {
          light: '1px solid #389355',
          dark: '1px solid #389355',
        },
        hover: {
          bg: {
            light: '#EDF7EF',
            dark: '#122617',
          },
          text: { light: '#389355', dark: '#389355' },
          border: {
            light: '1px solid #389355',
            dark: '1px solid #389355',
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: { light: '#389355', dark: '#389355' },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: '#EDF7EF', dark: '#122617' },
          text: { light: '#389355', dark: '#389355' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    error: {
      contained: {
        bg: {
          light: '#B12425',
          dark: '#851919',
        },
        text: {
          light: theme.palette.error.contrastText,
          dark: theme.palette.error.contrastText,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: '#851919',
            dark: '#B12425',
          },
          text: { light: 'white', dark: 'white' },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: {
          light: 'transparent',
          dark: 'transparent',
        },
        text: {
          light: '#B12425',
          dark: '#B12425',
        },
        border: {
          light: '1px solid #B12425',
          dark: '1px solid #B12425',
        },
        hover: {
          bg: {
            light: '#FFF6F5',
            dark: '#2B100F',
          },
          text: { light: '#B12425', dark: '#B12425' },
          border: {
            light: '1px solid #B12425',
            dark: '1px solid #B12425',
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: { light: '#B12425', dark: '#B12425' },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: '#FFF6F5', dark: '#2B100F' },
          text: { light: '#B12425', dark: '#B12425' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    lightBlue: {
      contained: {
        bg: {
          light: theme.palette.secondary.secondary_20,
          dark: theme.palette.secondary.secondary_99,
        },
        text: {
          light: theme.palette.secondary.contrastText,
          dark: theme.palette.secondary.contrastTextBlack,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: theme.palette.secondary.secondary_10,
            dark: theme.palette.secondary.secondary_20,
          },
          text: {
            light: 'white',
            dark: theme.palette.secondary.contrastTextBlack,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: {
          light: theme.palette.secondary.secondary_99,
          dark: theme.palette.secondary.secondary_95,
        },
        text: {
          light: theme.palette.secondary.secondary_20,
          dark: theme.palette.secondary.secondary_20,
        },
        border: {
          light: `1px solid ${theme.palette.secondary.secondary_20}`,
          dark: `1px solid ${theme.palette.secondary.secondary_20}`,
        },
        hover: {
          bg: {
            light: theme.palette.secondary.secondary_95,
            dark: theme.palette.secondary.secondary_90,
          },
          text: {
            light: theme.palette.secondary.secondary_20,
            dark: theme.palette.secondary.secondary_20,
          },
          border: {
            light: `1px solid ${theme.palette.secondary.secondary_20}`,
            dark: `1px solid ${theme.palette.secondary.secondary_20}`,
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: {
          light: theme.palette.secondary.secondary_20,
          dark: theme.palette.secondary.secondary_20,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: 'transparent', dark: 'transparent' },
          text: {
            light: theme.palette.secondary.secondary_10,
            dark: theme.palette.secondary.secondary_30,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    sunset: {
      contained: {
        bg: {
          light: 'linear-gradient(273deg, #FF6B2B -4.07%, #FF9810 105.64%)',
          dark: 'linear-gradient(273deg, #FF6B2B -4.07%, #FF9810 105.64%)',
        },
        text: {
          light: '#FFFFFF',
          dark: '#FFFFFF',
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: 'linear-gradient(273deg, #E65A1F -4.07%, #E68700 105.64%)',
            dark: 'linear-gradient(273deg, #E65A1F -4.07%, #E68700 105.64%)',
          },
          text: { light: '#FFFFFF', dark: '#FFFFFF' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    ocean: {
      contained: {
        bg: {
          light: 'linear-gradient(273deg, #1FA2F3 -4.07%, #02436B 105.64%)',
          dark: 'linear-gradient(273deg, #1FA2F3 -4.07%, #02436B 105.64%)',
        },
        text: {
          light: '#FFFFFF',
          dark: '#FFFFFF',
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: 'linear-gradient(273deg, #1891DA -4.07%, #013254 105.64%)',
            dark: 'linear-gradient(273deg, #1891DA -4.07%, #013254 105.64%)',
          },
          text: { light: '#FFFFFF', dark: '#FFFFFF' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    sky: {
      contained: {
        bg: {
          light: 'linear-gradient(273deg, #55B8F6 -4.07%, #0486D7 105.64%)',
          dark: 'linear-gradient(273deg, #55B8F6 -4.07%, #0486D7 105.64%)',
        },
        text: {
          light: '#FFFFFF',
          dark: '#FFFFFF',
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: 'linear-gradient(273deg, #3FA7E8 -4.07%, #0375BE 105.64%)',
            dark: 'linear-gradient(273deg, #3FA7E8 -4.07%, #0375BE 105.64%)',
          },
          text: { light: '#FFFFFF', dark: '#FFFFFF' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    lightBlueContained: {
      contained: {
        bg: {
          light: '#E1F2FD',
          dark: '#E1F2FD',
        },
        text: {
          light: '#0486D7',
          dark: '#0486D7',
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: {
            light: '#C5E5FB',
            dark: '#C5E5FB',
          },
          text: { light: '#0486D7', dark: '#0486D7' },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
    default: {
      contained: {
        bg: {
          light: theme.palette.background.default,
          dark: theme.palette.background.paper,
        },
        text: { light: theme.palette.text.primary, dark: 'white' },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: theme.palette.grey[500], dark: theme.palette.grey[700] },
          text: { light: 'white', dark: 'white' },
          border: { light: 'none', dark: 'none' },
        },
      },
      outlined: {
        bg: {
          light: theme.palette.background.default,
          dark: theme.palette.background.paper,
        },
        text: { light: theme.palette.text.primary, dark: 'white' },
        border: {
          light: `1px solid ${theme.palette.background.default}`,
          dark: `1px solid ${theme.palette.background.paper}`,
        },
        hover: {
          bg: { light: theme.palette.grey[500], dark: theme.palette.grey[700] },
          text: { light: 'white', dark: 'white' },
          border: {
            light: `1px solid ${theme.palette.grey[500]}`,
            dark: `1px solid ${theme.palette.grey[700]}`,
          },
        },
      },
      transparent: {
        bg: { light: 'transparent', dark: 'transparent' },
        text: {
          light: theme.palette.text.primary,
          dark: theme.palette.text.primary,
        },
        border: { light: 'none', dark: 'none' },
        hover: {
          bg: { light: 'transparent', dark: 'transparent' },
          text: {
            light: theme.palette.text.secondary,
            dark: theme.palette.text.secondary,
          },
          border: { light: 'none', dark: 'none' },
        },
      },
    },
  };

  const themeMode = theme.palette.mode;
  const colorVariant =
    colorMapping[color]?.[variant] || colorMapping.default[variant];

  const bgValue = colorVariant.bg[themeMode];
  const hoverBgValue = colorVariant.hover.bg[themeMode];

  // Check if the background is a gradient
  const isGradient = bgValue && bgValue.includes('gradient');
  const isHoverGradient = hoverBgValue && hoverBgValue.includes('gradient');

  // For gradient buttons, we use a pseudo-element technique for smooth transitions
  if (isGradient) {
    return {
      position: 'relative',
      background: `${bgValue} !important`,
      backgroundColor: 'transparent !important',
      color: colorVariant.text[themeMode],
      border: colorVariant.border[themeMode],
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: isHoverGradient ? hoverBgValue : bgValue,
        opacity: 0,
        transition: 'opacity 0.3s ease-in-out',
        zIndex: 0,
      },
      '&:hover::before': {
        opacity: 1,
      },
      '&:hover': {
        color: colorVariant.hover.text[themeMode],
        border: colorVariant.hover.border[themeMode],
      },
      // Ensure content is above the pseudo-element
      '& > *': {
        position: 'relative',
        zIndex: 1,
      },
    };
  }

  // For non-gradient buttons, use standard transitions
  return {
    backgroundColor: bgValue,
    color: colorVariant.text[themeMode],
    border: colorVariant.border[themeMode],
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: hoverBgValue,
      color: colorVariant.hover.text[themeMode],
      border: colorVariant.hover.border[themeMode],
    },
  };
};

// Helper function for generating size styles
const generateSizeStyles = (size, theme) => {
  const sizeMapping = {
    small: {
      fontSize: theme.spacing(4),
      padding: `${theme.spacing(1.5)} ${theme.spacing(5)}`,
      [theme.breakpoints.down('xl')]: {
        padding: `${theme.spacing(1.25)} ${theme.spacing(4)}`,
        fontSize: theme.spacing(3.75),
      },
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.spacing(3.5),
      },
      [theme.breakpoints.down('md')]: {
        fontSize: theme.spacing(3),
      },
    },
    medium: {
      fontSize: theme.spacing(4),
      padding: theme.spacing(2.75, 5),
      [theme.breakpoints.down('xl')]: {
        padding: theme.spacing(2, 4.25),
        fontSize: theme.spacing(3.75),
      },
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.spacing(3.5),
      },
      [theme.breakpoints.down('md')]: {
        fontSize: theme.spacing(3),
      },
    },
    large: {
      fontSize: theme.spacing(4),
      padding: `${theme.spacing(4)} ${theme.spacing(20)}`,
      [theme.breakpoints.down('xl')]: {
        padding: `${theme.spacing(3)} ${theme.spacing(16)}`,
        fontSize: theme.spacing(3.75),
      },
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.spacing(3.5),
        padding: `${theme.spacing(2.75)} ${theme.spacing(14)}`,
      },
      [theme.breakpoints.down('md')]: {
        padding: `${theme.spacing(2.5)} ${theme.spacing(11)}`,
        fontSize: theme.spacing(3),
      },
    },
  };

  return sizeMapping[size] || sizeMapping.medium;
};

const BaseButton = ({
  children,
  sx = {},
  onClick,
  color = 'main',
  link = false,
  to = '',
  disabled = false,
  loading = false,
  variant = 'contained',
  type = '',
  arrowIcon = false,
  href = '',
  target = '',
  size = 'medium',
  width = 'auto',
  icon,
  component,
}) => {
  const theme = useTheme();
  const colorStyles = generateColorStyles(color, variant, theme);
  const sizeStyles = generateSizeStyles(size, theme);

  // Width styles
  const widthStyles = {
    width: width === 'full' ? '100%' : 'auto',
  };

  return (
    <CustomButton
      variant={variant === 'transparent' ? 'text' : variant}
      type={type}
      sx={{
        ...colorStyles,
        ...sizeStyles,
        ...widthStyles,
        '&:disabled': {
          background: 'none !important',
          backgroundColor:
            variant === 'transparent' || variant === 'text'
              ? 'transparent !important'
              : theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.12) !important'
              : 'rgba(0, 0, 0, 0.12) !important',
          color:
            theme.palette.mode === 'dark'
              ? '#616161 !important'
              : 'rgba(0, 0, 0, 0.26) !important',
          boxShadow: 'none',
          '&::before': {
            display: 'none',
          },
        },
        ...sx,
      }}
      disabled={disabled}
      onClick={onClick}
      component={component || (link ? CustomButtonLink : 'button')}
      to={link ? to : undefined}
      href={href}
      target={target}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            opacity: loading ? 0 : 1,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {icon && (
            <Box
              component="span"
              sx={{
                mr: children ? 2 : 0,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {icon}
            </Box>
          )}
          {children}
          {arrowIcon && <ExportSquareCustom size="22px" />}
        </Box>
        {loading && (
          <CircularProgress
            size={18}
            sx={{
              position: 'absolute',
              // left: '50%',
              // top: '50%',
              transform: 'translate(-50%, -50%)',
              color: !disabled
                ? theme.palette.mode === 'dark'
                  ? '#333'
                  : '#fff'
                : theme.palette.primary.main,
            }}
          />
        )}
      </Box>
    </CustomButton>
  );
};

BaseButton.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  onClick: PropTypes.func,
  color: PropTypes.string,
  link: PropTypes.bool,
  to: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  variant: PropTypes.string,
  type: PropTypes.string,
  arrowIcon: PropTypes.bool,
  href: PropTypes.string,
  target: PropTypes.string,
  size: PropTypes.string,
  width: PropTypes.string,
  icon: PropTypes.node,
  component: PropTypes.elementType,
};

export default BaseButton;
